import { createContext, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { CustomInputScopeSchema } from "@/types/general";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import PrefrenceFormRenderer from "./PrefrenceFormRenderer";
import { child_spesific_key } from "@/config/endpoints";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";

const prefrenceSchema = z.object({
  customInputScopes: z.array(CustomInputScopeSchema).optional(),
});

type PrefrenceFormState = {
  errors: any;
  control: any;
  getValues: any;
  setValue: any;
};

const DealPreviewFormContext = createContext<PrefrenceFormState | null>(null);

function useDealPreviewForm(): PrefrenceFormState {
  const ctx = useContext(DealPreviewFormContext);
  if (!ctx) throw new Error("useDealPreviewForm used outside provider");
  return ctx;
}

type Props = {
  moduleScopes: { nicheModelName: string; nicheItemId: number }[];
  onScopesChange: (scopes: any[]) => void;
};

export default function DealPreviewPrefrences({
  moduleScopes,
  onScopesChange,
}: Props) {
  const { spesificFields } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    type: "SELECTOR",
  });

  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(prefrenceSchema),
    mode: "onChange",
    defaultValues: { customInputScopes: [] },
  });

  const scopes = watch("customInputScopes");

  useEffect(() => {
    onScopesChange(scopes ?? []);
  }, [scopes, onScopesChange]);

  if (!spesificFields?.length) return null;

  const formState: PrefrenceFormState = {
    errors,
    control,
    getValues,
    setValue,
  };

  return (
    <DealPreviewFormContext.Provider value={formState}>
      <Card className="rounded-2xl bg-white border border-[#e6e8e7] overflow-hidden shadow-sm">
        <CardHeader className="px-4 py-3 bg-gradient-to-r from-[#f7f9f8] to-white border-b border-[#f0f1f0]">
          <CardTitle className="text-[13px] font-semibold text-[#051912]">
            Preferences{" "}
            <span className="font-normal text-[#9ba3a0]">(optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-3">
          <PrefrenceFormRenderer
            hook={useDealPreviewForm}
            modelName="child"
            queryKey={child_spesific_key}
            moduleScopes={moduleScopes}
          />
        </CardContent>
      </Card>
    </DealPreviewFormContext.Provider>
  );
}
